import React, { PureComponent } from 'react';
import {
    oneOf,
    string,
    shape,
    number,
    oneOfType,
    bool,
    func,
} from 'prop-types';
import compose from 'lodash/flowRight';
import { defineMessages, injectIntl } from 'react-intl';
import { withGTM } from 'components';
import { withListingData, withFeatureFlags } from 'connectors';
import { intlShape } from 'shapes';
import RefinementList from '../RefinementList/RefinementList';
import ToggleRefinement from '../ToggleRefinement/ToggleRefinement';
import RangeFilter from '../RangeFilter/RangeFilter';
import OLDRangeFilter from '../RangeFilter/OLDRangeFilter';
import ToggleList from '../ToggleList/ToggleList';
import ColorList from '../ColorList/ColorList';
import CategoryFilter from '../CategoryFilter';

class FacetFilter extends PureComponent {
    static propTypes = {
        className: string,
        facet: shape({
            operator: oneOf(['or', 'and']),
            attributeName: string,
            label: string,
            id: oneOfType([string, number]),
            type: oneOf([
                'checkbox',
                'bool',
                'range',
                'toggleList',
                'color',
                'categoryList',
            ]),
            index: number,
        }).isRequired,
        zIndex: number,
        maxResults: number.isRequired,
        initialResultsLimit: number.isRequired,
        resultsLimitIncrement: number.isRequired,
        hasReactFilterSlider: bool.isRequired,
        openedByDefault: bool,
        pushToGTM: func.isRequired,
        mobileWidth: number.isRequired,
        intl: intlShape.isRequired,
    };

    static defaultProps = {
        className: '',
        zIndex: 0,
        openedByDefault: true,
    };

    constructor(props) {
        super(props);

        this.state = {
            limit: props.maxResults,
            nbOfRefinementListDisplayedResults: props.initialResultsLimit,
            nbOfColorListDisplayedResults: props.initialResultsLimit,
        };
    }

    handleShowMoreRefinementListItems = () => {
        this.setState({
            nbOfRefinementListDisplayedResults:
                this.state.nbOfRefinementListDisplayedResults +
                this.props.resultsLimitIncrement,
        });
    };

    handleShowMoreColorListItems = () => {
        this.setState({
            nbOfColorListDisplayedResults:
                this.state.nbOfColorListDisplayedResults +
                this.props.resultsLimitIncrement,
        });
    };

    pushDataToGTM = ({
        value,
        filterMin = null,
        filterMax = null,
        filterQuery = null,
    }) => {
        const { facet } = this.props;
        this.props.pushToGTM({
            event: 'filter_results',
            filter_id:
                typeof facet.id === 'string'
                    ? facet.id
                    : `cat_attr_${facet.id}`,
            filter_name: defineMessages(facet.labelKey),
            filter_value: value,
            filter_value_min: filterMin,
            filter_value_max: filterMax,
            filter_type: facet.type.toLowerCase(),
            filter_value_query: filterQuery,
        });
    };

    isRangeType = () => {
        const { facet } = this.props;
        return facet.type === 'range';
    };

    isRefinementList = () => {
        const { facet } = this.props;
        return facet.type === 'checkbox' || facet.operator === 'or';
    };

    isToggleList = () => {
        const { facet } = this.props;
        return facet.type === 'toggleList';
    };

    isToggle = () => this.props.facet.type === 'bool';

    isColor = () => this.props.facet.type === 'color';

    isCategoryFilter = () => this.props.facet.type === 'categoryList';

    render() {
        const {
            facet,
            zIndex,
            className,
            openedByDefault,
            hasReactFilterSlider,
            mobileWidth,
            intl,
        } = this.props;
        const {
            limit,
            nbOfRefinementListDisplayedResults,
            nbOfColorListDisplayedResults,
        } = this.state;

        const label = facet.labelKey
            ? intl.formatMessage(defineMessages(facet.labelKey.label))
            : facet.label;

        if (this.isRangeType()) {
            if (hasReactFilterSlider) {
                return (
                    <RangeFilter
                        className={className}
                        label={label}
                        attribute={facet.attributeName}
                        style={{
                            order: facet.order,
                            zIndex,
                        }}
                        openedByDefault={openedByDefault}
                        id={`${facet.id}`}
                        canShowNoResultLabel={facet.attributeName !== 'price'}
                        onFilterSelected={this.pushDataToGTM}
                    />
                );
            }

            return (
                <OLDRangeFilter
                    className={className}
                    label={label}
                    attribute={facet.attributeName}
                    style={{
                        order: facet.order,
                        zIndex,
                    }}
                    openedByDefault={openedByDefault}
                    id={`${facet.id}`}
                    onFilterSelected={this.pushDataToGTM}
                />
            );
        }
        if (this.isToggle()) {
            const title = facet.labelKey?.title
                ? intl.formatMessage(defineMessages(facet.labelKey.title))
                : facet.title;
            return (
                <ToggleRefinement
                    className={className}
                    title={title}
                    label={label}
                    count={facet.count}
                    attribute={facet.attributeName}
                    value="true"
                    style={{
                        order: facet.order,
                        zIndex,
                    }}
                    openedByDefault={openedByDefault}
                    onFilterSelected={this.pushDataToGTM}
                />
            );
        }
        if (this.isToggleList()) {
            return (
                <ToggleList
                    className={className}
                    label={label}
                    items={facet.items}
                    attribute={facet.attributeName}
                    style={{
                        order: facet.order,
                        zIndex,
                    }}
                    openedByDefault={openedByDefault}
                    onFilterSelected={this.pushDataToGTM}
                />
            );
        }
        if (this.isColor()) {
            return (
                <ColorList
                    className={className}
                    label={label}
                    attribute={facet.attributeName}
                    style={{
                        order: facet.order,
                        zIndex,
                    }}
                    limit={limit}
                    nbOfDisplayedResults={nbOfColorListDisplayedResults}
                    onShowMoreResults={this.handleShowMoreColorListItems}
                    openedByDefault={openedByDefault}
                    onFilterSelected={this.pushDataToGTM}
                />
            );
        }
        if (this.isRefinementList()) {
            return (
                <RefinementList
                    className={className}
                    style={{
                        order: facet.order,
                        zIndex,
                    }}
                    label={label}
                    attribute={facet.attributeName}
                    limit={limit}
                    nbOfDisplayedResults={nbOfRefinementListDisplayedResults}
                    onShowMoreResults={this.handleShowMoreRefinementListItems}
                    openedByDefault={openedByDefault}
                    onFilterSelected={this.pushDataToGTM}
                    mobileWidth={mobileWidth}
                />
            );
        }
        if (this.isCategoryFilter()) {
            return (
                <CategoryFilter
                    className={className}
                    attribute={facet.attributeName}
                    onCategorySelected={this.pushDataToGTM}
                />
            );
        }

        return null;
    }
}

export default compose(
    withFeatureFlags({
        resultsLimitIncrement: 'instantSearch.filters.resultsLimitIncrement',
        initialResultsLimit: 'instantSearch.filters.initialResultsLimit',
        maxResults: 'instantSearch.filters.maxResults',
        hasReactFilterSlider: 'browsing.react.filter.slider',
    }),
    withGTM,
    withListingData,
    injectIntl,
)(FacetFilter);
